// Super Admin — Inquiry Detail Page (CRM)
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, Building2, MapPin, Loader2, Plus,
  Calendar, Clock, MessageSquare, Activity, StickyNote, User,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "converted", "closed", "lost", "not_interested", "duplicate", "spam"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const FLAGS = ["hot_lead", "important", "follow_up_due", "payment_expected", "demo_required", "proposal_required", "callback_required", "not_interested", "duplicate", "spam"];
const FOLLOWUP_TYPES = ["call", "whatsapp", "email", "meeting", "demo", "proposal", "payment_follow_up", "other"];
const FOLLOWUP_STATUSES = ["pending", "completed", "missed", "rescheduled"];

const title = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminInquiryDetailPage() {
  const { inquiryId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);

  const [fuOpen, setFuOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [fuForm, setFuForm] = useState({
    followup_date: new Date().toISOString().slice(0, 10),
    followup_time: "", followup_type: "call", followup_status: "pending",
    remarks: "", next_followup_required: false,
    next_followup_date: "", next_followup_time: "",
  });
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get(`/admin/inquiries/${inquiryId}`);
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [inquiryId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/inquiries/assignable-users");
        setUsers(data.data.users || []);
      } catch { /* silent */ }
    })();
  }, []);

  const lc = useLifecycleActions({
    basePath: "/admin/inquiries",
    labelOf: (row) => `"${row.full_name}"`,
    onDone: () => nav("/app/super-admin/inquiries"),
  });

  if (!data) return <DashboardLayout title="Inquiry"><LoadingBlock /></DashboardLayout>;
  const r = data.inquiry;
  const assignedName = r.assigned_to_user?.full_name || r.assigned_to_user?.email || "Unassigned";

  const changeStatus = async (v) => {
    try { await api.patch(`/admin/inquiries/${r.id}/status`, { status: v }); toast.success("Inquiry status updated successfully."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const changePriority = async (v) => {
    try { await api.patch(`/admin/inquiries/${r.id}/flag`, { priority: v }); toast.success("Priority updated."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const changeFlag = async (v) => {
    try { await api.patch(`/admin/inquiries/${r.id}/flag`, { flag: v === "none" ? "" : v }); toast.success("Flag updated."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const changeAssign = async (v) => {
    try { await api.patch(`/admin/inquiries/${r.id}/assign`, { assigned_to: v === "unassigned" ? null : v }); toast.success("Inquiry assigned successfully."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const addFollowup = async () => {
    if (!fuForm.followup_date) return toast.error("Follow-up date required.");
    setSaving(true);
    try {
      const body = { ...fuForm };
      if (!body.next_followup_required) { delete body.next_followup_date; delete body.next_followup_time; }
      await api.post(`/admin/inquiries/${r.id}/followups`, body);
      toast.success("Follow-up added successfully.");
      setFuOpen(false);
      setFuForm({ ...fuForm, remarks: "", next_followup_required: false, next_followup_date: "", next_followup_time: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return toast.error("Note text required.");
    setSaving(true);
    try {
      await api.post(`/admin/inquiries/${r.id}/notes`, { note_text: noteText.trim() });
      toast.success("Note added successfully.");
      setNoteText(""); setNoteOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Inquiry Detail" breadcrumbs={["Super Admin", "Inquiries", r.inquiry_code || "Detail"]}>
      <button onClick={() => nav("/app/super-admin/inquiries")}
        className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#101876]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Inquiries
      </button>

      <div className="grid gap-4 lg:grid-cols-[1fr,340px]">
        {/* LEFT column */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="inquiry-basic">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs font-mono text-slate-400">{r.inquiry_code}</div>
                <div className="mt-1 text-2xl font-black text-slate-900">{r.full_name}</div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                  {r.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>}
                  {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                  {r.alternate_mobile && <span className="text-slate-400">alt {r.alternate_mobile}</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                  {r.organization_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {r.organization_name}{r.designation ? ` · ${r.designation}` : ""}</span>}
                  {(r.city || r.state) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {[r.city, r.state].filter(Boolean).join(", ")}</span>}
                </div>
              </div>
              <RowActionsMenu
                testId={`inquiry-detail-actions-${r.id}`}
                status={r.is_deleted ? "deleted" : "active"}
                onDelete={() => lc.open("delete", r)}
                onRestore={() => lc.open("restore", r)}
              />
            </div>
            {r.message && (
              <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                {r.message}
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
              <MetaBox label="Inquiry Type" value={title(r.inquiry_type)} />
              <MetaBox label="Source" value={title(r.source)} />
              <MetaBox label="Course Interested" value={r.course_interested || "—"} />
              <MetaBox label="Lead Stage" value={title(r.lead_stage)} />
            </div>
          </div>

          {/* Follow-ups */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="inquiry-followups">
            <div className="mb-3 flex items-center justify-between">
              <div className="font-bold text-slate-900 inline-flex items-center gap-2"><Calendar className="h-4 w-4" /> Follow-up History</div>
              <button onClick={() => setFuOpen(true)} data-testid="inquiry-add-followup"
                className="inline-flex items-center gap-1 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]">
                <Plus className="h-3 w-3" /> Add Follow-up
              </button>
            </div>
            {data.followups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">No follow-ups yet</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.followups.map((f) => (
                  <li key={f.id} className="py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-800">
                        {title(f.followup_type)} · <span className="text-slate-500">{f.followup_date}{f.followup_time ? ` ${f.followup_time}` : ""}</span>
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${f.followup_status === "completed" ? "bg-emerald-100 text-emerald-700" : f.followup_status === "missed" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                        {title(f.followup_status)}
                      </span>
                    </div>
                    {f.remarks && <div className="mt-1 text-slate-600">{f.remarks}</div>}
                    <div className="mt-1 text-[10px] text-slate-400">by {f.created_by_name || "—"}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Notes */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="inquiry-notes">
            <div className="mb-3 flex items-center justify-between">
              <div className="font-bold text-slate-900 inline-flex items-center gap-2"><StickyNote className="h-4 w-4" /> Internal Notes</div>
              <button onClick={() => setNoteOpen(true)} data-testid="inquiry-add-note"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">
                <Plus className="h-3 w-3" /> Add Note
              </button>
            </div>
            {data.notes.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">No notes yet</div>
            ) : (
              <ul className="space-y-2">
                {data.notes.map((n) => (
                  <li key={n.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs">
                    <div className="whitespace-pre-wrap text-slate-700">{n.note_text}</div>
                    <div className="mt-1 text-[10px] text-slate-400">by {n.added_by_name || "—"} · {new Date(n.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Activity timeline */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="inquiry-activity">
            <div className="mb-3 font-bold text-slate-900 inline-flex items-center gap-2"><Activity className="h-4 w-4" /> Activity Timeline</div>
            {data.activity.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">No activity yet</div>
            ) : (
              <ol className="relative border-l border-slate-200 pl-4">
                {data.activity.map((a) => (
                  <li key={a.id} className="mb-3 ml-1">
                    <div className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-[#101876] bg-white" />
                    <div className="text-xs font-semibold text-slate-800">{title(a.action_type)}</div>
                    {(a.old_value || a.new_value) && (
                      <div className="text-[10px] text-slate-500">
                        {a.old_value ? `${a.old_value} → ` : ""}{a.new_value || ""}
                      </div>
                    )}
                    {a.remarks && <div className="text-[11px] text-slate-600">{a.remarks}</div>}
                    <div className="text-[10px] text-slate-400">
                      {new Date(a.performed_at).toLocaleString()} · {a.performed_by_name || "System"}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* RIGHT column */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-3 text-sm font-bold text-slate-900">Status & Assignment</div>
            <div className="space-y-3">
              <FieldSel label="Status" v={r.status} on={changeStatus} opts={STATUSES.map((s) => ({ v: s, l: title(s) }))} />
              <FieldSel label="Priority" v={r.priority} on={changePriority} opts={PRIORITIES.map((s) => ({ v: s, l: title(s) }))} />
              <FieldSel label="Flag" v={r.flag || "none"} on={changeFlag}
                opts={[{ v: "none", l: "No flag" }, ...FLAGS.map((s) => ({ v: s, l: title(s) }))]} />
              <FieldSel label="Assigned To" v={r.assigned_to || "unassigned"} on={changeAssign}
                opts={[{ v: "unassigned", l: "Unassigned" }, ...users.map((u) => ({ v: u.id, l: `${u.full_name || u.email} · ${title(u.role_code)}` }))]} />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-xs">
            <div className="mb-2 text-sm font-bold text-slate-900">Timeline</div>
            <MetaLine label="Created" value={new Date(r.created_at).toLocaleString()} />
            <MetaLine label="Last Updated" value={new Date(r.updated_at).toLocaleString()} />
            {r.contacted_at && <MetaLine label="First Contacted" value={new Date(r.contacted_at).toLocaleString()} />}
            {r.next_followup_date && <MetaLine label="Next Follow-up" value={`${r.next_followup_date}${r.next_followup_time ? ` ${r.next_followup_time}` : ""}`} />}
            {r.converted_date && <MetaLine label="Converted" value={new Date(r.converted_date).toLocaleString()} />}
            {r.closed_date && <MetaLine label="Closed" value={new Date(r.closed_date).toLocaleString()} />}
            {r.lost_reason && <MetaLine label="Lost Reason" value={r.lost_reason} />}
            <MetaLine label="Assigned" value={assignedName} />
          </div>
        </aside>
      </div>

      {/* Add follow-up dialog */}
      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent className="sm:max-w-md" data-testid="followup-dialog">
          <DialogHeader><DialogTitle>Add Follow-up</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <FieldIn label="Date *" type="date" v={fuForm.followup_date} on={(v) => setFuForm({ ...fuForm, followup_date: v })} tid="fu-date" />
            <FieldIn label="Time" type="time" v={fuForm.followup_time} on={(v) => setFuForm({ ...fuForm, followup_time: v })} />
            <FieldSel label="Type" v={fuForm.followup_type} on={(v) => setFuForm({ ...fuForm, followup_type: v })}
              opts={FOLLOWUP_TYPES.map((s) => ({ v: s, l: title(s) }))} />
            <FieldSel label="Status" v={fuForm.followup_status} on={(v) => setFuForm({ ...fuForm, followup_status: v })}
              opts={FOLLOWUP_STATUSES.map((s) => ({ v: s, l: title(s) }))} />
            <div className="col-span-2 space-y-1">
              <Label>Remarks</Label>
              <Textarea rows={2} value={fuForm.remarks} onChange={(e) => setFuForm({ ...fuForm, remarks: e.target.value })} data-testid="fu-remarks" />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-xs">
              <input type="checkbox" checked={fuForm.next_followup_required}
                onChange={(e) => setFuForm({ ...fuForm, next_followup_required: e.target.checked })} />
              Schedule next follow-up
            </label>
            {fuForm.next_followup_required && (
              <>
                <FieldIn label="Next Date" type="date" v={fuForm.next_followup_date} on={(v) => setFuForm({ ...fuForm, next_followup_date: v })} />
                <FieldIn label="Next Time" type="time" v={fuForm.next_followup_time} on={(v) => setFuForm({ ...fuForm, next_followup_time: v })} />
              </>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setFuOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={addFollowup} disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid="fu-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Follow-up
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="note-dialog">
          <DialogHeader><DialogTitle>Add Internal Note</DialogTitle></DialogHeader>
          <Textarea rows={5} value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Not visible to customer — team-only note." data-testid="note-text" />
          <DialogFooter>
            <button onClick={() => setNoteOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={addNote} disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid="note-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Note
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lc.dialog}
    </DashboardLayout>
  );
}

function FieldIn({ label, v, on, type = "text", tid }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid} />
    </div>
  );
}
function FieldSel({ label, v, on, opts }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      <Select value={v} onValueChange={on}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function MetaBox({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 truncate text-xs font-semibold text-slate-800">{value}</div>
    </div>
  );
}
function MetaLine({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-semibold text-slate-800">{value}</span>
    </div>
  );
}
